/**
 * apiBridge.ts — one door for the API fallback contract.
 *
 * Built from `claude/PROPOSAL_the_fallback_contract.md`, with the per-route gate
 * folded in. **This is the only file in `src/` allowed to call `api.X()`**; an
 * ESLint rule fails the build on a raw call anywhere else (see `package.json`,
 * `no-restricted-properties`).
 *
 * ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * The fallback does not do what everyone believes it does. It covers a backend
 * that is DOWN. It does nothing at all about a backend that is UP and WRONG —
 * and a backend that is up and wrong is indistinguishable, at the call site,
 * from a backend that is up and right. That belief was load-bearing across 33
 * files and 112 raw calls, in thirteen different spellings, and thirteen
 * spellings of one question is a question nobody can audit.
 *
 * ⚠️ THOSE TWO NUMBERS ARE ESLINT'S, NOT A GREP'S, AND THEY ARE STAMPED. Measured
 * 2026-08-25 by turning the guard below on with NO exemptions and running
 * `CI=true npm run build`: 117 hits across 34 files, of which 5 hits in 1 file are
 * this file's own calls, leaving **112 across 33**. A `grep -rEon` over `src/` says
 * 119 across 35, and it is not wrong — it counts a call written in a docblock in
 * `apiClient.ts` and a call in `apiClient.test.ts`, neither of which the compiler
 * ever sees. Different question, different number; use the one whose command is
 * written next to it.
 *
 * This file does not make a wrong backend detectable. Nothing at the call site
 * can. What it does is make every call site DECLARE which of three situations it
 * is in — bridged read, bridged write, or API-only — and make not declaring
 * inexpressible.
 *
 * ─── THE THREE DOORS ────────────────────────────────────────────────────────
 *
 *   bridgedRead   a read with a Supabase answer behind it.
 *   bridgedWrite  a write with a Supabase answer behind it, plus a declared
 *                 answer to "is this safe to repeat".
 *   apiOnlyRead   a read with NO Supabase answer possible. Returns the
 *                 discriminated result, so the caller cannot avoid handling
 *                 failure.
 *
 * ─── WHAT `SERVED` MEANS, AND WHY WRITES CANNOT USE THE SAME WORD ───────────
 *
 * A read is `served` iff the transport succeeded AND the body validated against
 * the caller's own schema. **An empty list is `served: true`, deliberately.** A
 * client that falls back on empty is consulting two sources of truth and showing
 * whichever spoke, which is worse than either alone: they can disagree and
 * nothing says which won.
 *
 * A write cannot be judged by its payload at all. A create may return an id, a
 * body, or 204 No Content; a patch may return nothing. There is no shape to
 * validate, so `applied` is **2xx and nothing more**. Demanding a payload is how
 * `submitLeaveRequest` came to treat a committed row as a failure and insert a
 * second one.
 */

import {
  api,
  apiConfigured,
  apiOnly,
  apiOnlyConfigured,
  getCurrentWorkspaceId,
} from './apiClient';

// ─── The per-route gate ─────────────────────────────────────────────────────
//
// The old gate is all-or-nothing: adding a workspace to `pulsehq_phase2_workspaces`
// turns on EVERY route at once, including routes reading a table that has never
// held a row. There is no setting between "all off" and "all on", so attendance
// would have gone live in the same instant as the routes that actually work.

/**
 * Route groups a workspace can be opted into, one at a time.
 *
 * A runtime array rather than a bare union, because the group has to be a value a
 * settings UI can list and a test can enumerate. The type is derived FROM the array,
 * so the two cannot drift apart.
 */
export const ALL_ROUTE_GROUPS = [
  'analytics',
  'announcements',
  'attendance',
  'billing',
  'channels',
  'company-goals',
  'crm',
  'departments',
  'employee-docs',
  'employees',
  'expenses',
  'kpi-checkins',
  'kpis',
  'leave',
  'lifecycle-tasks',
  'messages',
  'notifications',
  'performance-reviews',
  'profiles',
  'projects',
  'promotions',
  'recruitment',
  'resignations',
  'storage',
  'tasks',
  'terminations',
  'tickets',
  'training',
] as const;

export type RouteGroup = (typeof ALL_ROUTE_GROUPS)[number];

/**
 * Storage key for the per-route gate. Shape: `{ "<workspace-uuid>": ["leave"] }`.
 *
 * ⚠️ A workspace already in the OLD `pulsehq_phase2_workspaces` list migrates to
 * NO route groups enabled, not to all of them — which is why nothing reads the old
 * list to decide a route. Preserving the old behaviour would silently carry
 * "everything on" across the very change meant to end it.
 */
export const PHASE2_ROUTES_KEY = 'pulsehq_phase2_routes';

function lsGet(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Route groups enabled for the current workspace. Always an array. */
export function enabledRouteGroups(): string[] {
  const ws = getCurrentWorkspaceId();
  if (!ws) return [];
  const raw = lsGet(PHASE2_ROUTES_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
    const groups = (parsed as Record<string, unknown>)[ws];
    if (!Array.isArray(groups)) return [];
    return groups.filter((g): g is string => typeof g === 'string' && g.length > 0);
  } catch {
    return [];
  }
}

/**
 * True iff this route group may reach the backend right now.
 *
 * BOTH gates must pass: the old per-workspace opt-in (still enforced inside
 * `getApiBase()`) and the new per-route opt-in. That is deliberately stricter than
 * the proposal's "move the gate out of getApiBase()" — see the note in the module
 * header of `apiClient.ts`. Moving it today would un-gate the 112 raw call sites
 * that have NOT been converted, which is the opposite of what the gate is for.
 */
export function isRouteEnabled(group: RouteGroup): boolean {
  if (!apiConfigured()) return false;
  return enabledRouteGroups().includes(group);
}

// ─── Read results ───────────────────────────────────────────────────────────

export type ReadFailReason =
  /** The request never left: no base URL, or the gate is shut. */
  | 'not-configured'
  /** No response at all — dropped connection, timeout, CORS. */
  | 'transport'
  /** The server answered with a non-2xx status. */
  | 'http-error'
  /** 2xx, and the body was not the shape the caller asked for. */
  | 'malformed';

export type ApiRead<T> =
  | { served: true; data: T }
  | { served: false; reason: ReadFailReason; detail?: string };

/**
 * A caller's schema. Returns the parsed value, or `null` if the body is not the
 * agreed shape.
 *
 * ⚠️ `null` means MALFORMED, so a schema must never return `null` for a legitimately
 * empty answer. `(raw) => isItems(raw) ? raw.items : null` is right; returning `null`
 * for `items: []` would turn "the answer is nothing" into "the backend is broken",
 * which is the bug this file exists to remove, inverted.
 */
export type Schema<T> = (raw: unknown) => T | null;

// ─── Write results ──────────────────────────────────────────────────────────

export type WriteFailReason = 'not-configured' | 'transport' | 'http-error';

/**
 * How the caller declares whether a lost response may be retried.
 *
 * There is no default and no guess. `'upsert'` means the backend keys the write on
 * something natural — `(employee_id, date)` for a check-in — so doing it twice is
 * doing it once. `false` means it does not, and a repeat creates a second row that
 * nothing can tell apart from the first.
 */
export type Idempotency = 'upsert' | false;

export interface WriteInit<T> {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** REQUIRED. See `Idempotency`. Guessing this is how duplicates get made. */
  idempotent: Idempotency;
  /**
   * Optional parser for a success body. Purely informational: a schema that fails
   * here does NOT retract `applied`, because a write's outcome is its status code
   * and a body it could not parse is still a row that exists.
   */
  schema?: Schema<T>;
  /**
   * ⚠️ ACCOMMODATED, NOT IMPLEMENTED — and deliberately NOT sent anywhere.
   *
   * Option A (an idempotency key with a storage half — a key column or a shared
   * `idempotency_keys` table) is the eventual mechanism, and the ruling of
   * 2026-08-25 was that adopting it later must not mean reopening this signature.
   * So the field exists and the type is stable.
   *
   * Nothing consumes it. It is not put in a header and not put in the body, because
   * a key that travels to a backend with no storage behind it protects nothing while
   * looking exactly like protection — which is the disease this whole file treats.
   * `apiBridge.test.ts` asserts it never reaches the wire, so the day somebody wires
   * it up, that test goes red and makes them say so.
   */
  idempotencyKey?: string;
}

export type WriteOutcome<T> =
  | { ok: true; source: 'api' | 'fallback'; data?: T }
  | {
      ok: false;
      /**
       * True iff the request left and no answer came back, so the write MAY have
       * committed. Nothing has fallen back; the caller must tell the user.
       */
      unconfirmed: boolean;
      error: string;
      /**
       * The server's HTTP status, when it answered (0 or absent when it did not).
       *
       * ⛔ A STATUS IS NOT "THE WRITE DID NOT HAPPEN" FOR EVERY CALLER. A 502/504 from the
       * proxy can arrive while the backend is still finishing — for a mail send that means
       * the email may be gone. `sendMail` reads this to say "we couldn't confirm" instead
       * of "nothing was delivered" (2026-09-28).
       */
      status?: number;
    };

/**
 * What a person is told when a non-idempotent write's response was lost.
 *
 * Ugly, honest, and not a duplicate. Lis's ruling 2026-08-25, option C.
 */
export const UNCONFIRMED_WRITE_MESSAGE =
  'We could not confirm whether your request was saved. ' +
  'Reload the page and check before submitting again.';

// ─── The doors ──────────────────────────────────────────────────────────────

/**
 * Shared: run one API read and classify the outcome. Never throws.
 *
 * ⚠️ `ungated` picks the TRANSPORT, and it must agree with whichever guard the caller
 * used. `bridgedRead` checks `isRouteEnabled` (both gates) and reads through `api`;
 * `apiOnlyRead` checks `apiOnlyConfigured` (URL only) and must therefore read through
 * `apiOnly`, or its guard says yes and its transport says "Backend not configured" —
 * which surfaces as `transport`, i.e. "the server did not answer", about a request
 * that was never sent. **That is exactly what happened on the first run of
 * `mailAccounts.test.ts`**: the guard was ungated here and this line was not, so every
 * read failed while every write passed. The test is what found it.
 */
async function readOnce<T>(
  path: string,
  schema: Schema<T>,
  { ungated = false }: { ungated?: boolean } = {},
): Promise<ApiRead<T>> {
  const res = ungated ? await apiOnly.get<unknown>(path) : await api.get<unknown>(path);
  if (!res.ok) {
    // `status === 0` means fetch never got an answer. Callers of `readOnce` have
    // already established that the base URL resolves, so this is genuinely a
    // transport failure rather than "backend not configured".
    return {
      served: false,
      reason: res.status === 0 ? 'transport' : 'http-error',
      detail: res.error,
    };
  }
  const parsed = schema(res.data);
  if (parsed === null) {
    return { served: false, reason: 'malformed', detail: 'response did not match the expected shape' };
  }
  return { served: true, data: parsed };
}

/**
 * A read with a Supabase answer behind it.
 *
 * Returns `T` — there is no `res.ok` for anyone to check and no `if` for anyone to
 * get wrong. The empty-versus-malformed distinction is made once, in `schema`.
 */
export async function bridgedRead<T>(
  group: RouteGroup,
  path: string,
  schema: Schema<T>,
  fallback: () => Promise<T>,
): Promise<T> {
  if (!isRouteEnabled(group)) return fallback();
  const read = await readOnce(path, schema);
  if (read.served) return read.data;
  return fallback();
}

/**
 * A write with a Supabase answer behind it.
 *
 * The fallback returns `T` and THROWS on failure — the same shape the call sites
 * already had inside their `try`. A throw becomes `{ ok: false, unconfirmed: false }`.
 *
 * The policy, which is the whole reason this function exists:
 *
 *   not-configured / gate shut  fall back. The request never left.
 *   2xx                          applied. Do NOT fall back, whatever the body.
 *   http-error (4xx/5xx)         fall back. The server answered; it did not commit.
 *   transport (no response)      fall back ONLY if the caller declared it idempotent.
 *                                Otherwise surface it — the row may already exist.
 */
export async function bridgedWrite<T>(
  group: RouteGroup,
  path: string,
  init: WriteInit<T>,
  fallback: () => Promise<T>,
): Promise<WriteOutcome<T>> {
  const runFallback = async (): Promise<WriteOutcome<T>> => {
    try {
      const data = await fallback();
      return { ok: true, source: 'fallback', data };
    } catch (err) {
      return {
        ok: false,
        unconfirmed: false,
        error: err instanceof Error ? err.message : 'The change could not be saved.',
      };
    }
  };

  if (!isRouteEnabled(group)) return runFallback();

  const res =
    init.method === 'POST'
      ? await api.post<unknown>(path, init.body)
      : init.method === 'PATCH'
      ? await api.patch<unknown>(path, init.body)
      : init.method === 'PUT'
      ? await api.put<unknown>(path, init.body)
      : await api.delete<unknown>(path);

  if (res.ok) {
    // `applied` is 2xx and nothing more. A schema that cannot parse the body does
    // not retract that — the row exists either way, and falling back here is how a
    // duplicate gets made.
    const data = init.schema ? init.schema(res.data) ?? undefined : (res.data as T);
    return { ok: true, source: 'api', data };
  }

  if (res.status === 0) {
    // No response. The server may have committed already.
    if (init.idempotent === false) {
      return { ok: false, unconfirmed: true, error: UNCONFIRMED_WRITE_MESSAGE };
    }
    return runFallback();
  }

  // A real HTTP status means the server answered and did not commit.
  return runFallback();
}

/**
 * A read with NO Supabase answer possible.
 *
 * `owner.ts` is cross-workspace; Supabase RLS scopes every query to one tenant, so
 * a fallback could only ever show the owner their own workspace and call it "all
 * customers" — a wrong answer wearing a right one's clothes.
 *
 * Three properties, and each of them is load-bearing:
 *
 *   1. **No fallback argument exists**, so "forgot to add one" is not expressible.
 *   2. **It returns the discriminated result rather than `T`**, so the caller cannot
 *      render a table without first branching on `served`. For the owner console the
 *      correct behaviour on `served: false` is an explicit error state — never an
 *      empty table, which reads as "no customers".
 *   3. **It takes no ROUTE GROUP and is not route-gated.** For a bridged call, "gate
 *      off" means "use Supabase". Here there is no Supabase, so a route group would
 *      mean the console does not work at all.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ⚠️ IT *IS* STILL SUBJECT TO THE PER-WORKSPACE CUTOVER GATE. THE OLD      ║
 * ║     DOCSTRING SAID IT WAS NOT, AND THAT WAS FALSE.                       ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 * *Corrected 2026-08-28. The sentence removed was "It takes no route group and is
 * NOT gated" — true of the route-group gate, false of the per-workspace one:*
 *
 *     apiOnlyRead -> apiConfigured() -> getApiBase()
 *                                       if (!isPhase2EnabledForCurrentWorkspace())
 *                                           return '';
 *
 * So an api-only read returns `not-configured` on any workspace absent from
 * `localStorage['pulsehq_phase2_workspaces']` — **and nothing in the product can put
 * one there**; `setPhase2Workspaces` is exported and no component calls it. **The
 * owner console is in that state on the deployed build.**
 *
 * ⛔ **AND UNGATING THIS WAS CONSIDERED AND REJECTED, WHICH IS WHY THE COMMENT IS
 *    LONGER THAN THE CODE.** Measured on production 2026-08-28 16:29–16:31 UTC:
 *
 *        list_workspaces  -> 500, column subscriptions.stripe_price_id does not exist
 *        revenue_summary  -> OK
 *        usage_summary    -> OK
 *
 *    `OwnerDashboardPage` reports a banner only when `failures.length === 3`. So
 *    ungating would take one honest banner ("this is not an empty platform") and turn
 *    it into **no banner and a missing customer list** — the exact failure this file
 *    exists to prevent, arriving through this file. Lis's ruling: leave it gated;
 *    that is an owner-console decision and it does not ride in on a mail screen.
 *
 * If you are here because your api-only read returns `not-configured` on a workspace
 * that should work, the answer is `apiOnlyReadUngated` below — read its warning first.
 */
export async function apiOnlyRead<T>(path: string, schema: Schema<T>): Promise<ApiRead<T>> {
  if (!apiConfigured()) {
    return { served: false, reason: 'not-configured' };
  }
  return readOnce(path, schema);
}

/**
 * An api-only read that is ALSO exempt from the per-workspace cutover gate.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ TWO READS EXIST ON PURPOSE. PICK THE GATED ONE UNLESS YOU CAN SAY     ║
 * ║     WHY NOT.                                                             ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 * The cutover gate is a FALLBACK switch: "off" means *use Supabase*. That is right
 * wherever a Supabase path exists, and it is right for `apiOnlyRead`'s existing
 * caller too — the owner console is broken for a different reason, and silencing it
 * quietly would be worse than the banner it shows today.
 *
 * **It is wrong for a surface whose ONLY job is the API and which has no banner to
 * fall back to.** Connecting a mailbox is that: no Supabase equivalent, no fixture,
 * nothing to show. "Gate off" there means the screen renders "no mailbox connected"
 * for ever, on a workspace nobody remembered to add to a localStorage array that no
 * part of the product can write.
 *
 * ⚠️ **THE GATE IS NOT HOISTED OUT OF `getApiBase()`, DELIBERATELY.** `apiClient.ts`
 * says why: it is the only reason the 112 unconverted raw `api.X()` calls
 * short-circuit at all, and moving it would un-gate every one of them in one commit.
 * This function sits beside the gate rather than removing it.
 *
 * **Before using this, answer: what does this screen show if the call does not
 * happen?** If the answer is "the Supabase version" or "an honest error banner", use
 * `apiOnlyRead`. If it is "something indistinguishable from an empty, working
 * screen", this is the one.
 */
export async function apiOnlyReadUngated<T>(
  path: string,
  schema: Schema<T>,
): Promise<ApiRead<T>> {
  if (!apiOnlyConfigured()) {
    return { served: false, reason: 'not-configured' };
  }
  return readOnce(path, schema, { ungated: true });
}

/**
 * A WRITE with no Supabase answer possible. The mirror of `apiOnlyRead`.
 *
 * *Added 2026-08-28 for the mail settings screen — connecting a mailbox is the first
 * write in this product that Supabase cannot perform, because the credential goes to
 * Vault through a FastAPI route.*
 *
 * ⛔ **NO `fallback` ARGUMENT, exactly as the read has none.** `bridgedWrite` takes
 * one because "the API refused" can legitimately mean "do it in Supabase instead".
 * Here it cannot, so the parameter does not exist and a caller cannot smuggle a
 * Supabase path in by passing one.
 *
 * ⚠️ **`idempotent` IS STILL REQUIRED, and it is not ceremony.** Without a fallback,
 * a lost response has exactly one honest outcome — say so — and the caller must have
 * already decided whether a repeat is safe. `POST /mail/accounts` is `false`: two
 * calls make two mailbox rows and two Vault secrets, and nothing downstream can tell
 * them apart.
 *
 * Returns the same `WriteOutcome` as `bridgedWrite`, minus `source: 'fallback'`,
 * which is unreachable here by construction.
 */
export async function apiOnlyWrite<T>(
  path: string,
  init: WriteInit<T>,
): Promise<WriteOutcome<T>> {
  if (!apiOnlyConfigured()) {
    return {
      ok: false,
      unconfirmed: false,
      error: 'The backend is not reachable from this build, so this could not be saved.',
    };
  }

  const res =
    init.method === 'POST'
      ? await apiOnly.post<unknown>(path, init.body)
      : init.method === 'PATCH'
      ? await apiOnly.patch<unknown>(path, init.body)
      : init.method === 'DELETE'
      ? await apiOnly.delete<unknown>(path)
      : await apiOnly.post<unknown>(path, init.body);

  if (res.ok) {
    // 2xx and nothing more. A body the schema cannot read does not retract the write
    // — the row exists either way. Same rule as `bridgedWrite`.
    const data = init.schema ? init.schema(res.data) ?? undefined : (res.data as T);
    return { ok: true, source: 'api', data };
  }

  if (res.status === 0) {
    // No answer came back, so the server MAY have committed. There is nothing to fall
    // back to, so the only honest thing is to say we do not know.
    return {
      ok: false,
      unconfirmed: init.idempotent === false,
      error: init.idempotent === false ? UNCONFIRMED_WRITE_MESSAGE : res.error,
    };
  }

  // A real status means the server answered and did not commit. Its message is the
  // caller's message: `mail_connect` writes sentences a person can act on, and
  // replacing them here with something generic is a downgrade.
  return { ok: false, unconfirmed: false, error: res.error, status: res.status };
}
